import { useState } from "react";
import { Icon } from "@iconify/react";
import { Collapse } from "antd";
import type { Medication } from "@/shared/types";
import { Button } from "@/shared/components/button";
import { cn } from "@/shared/lib/utils";
import { OriginTag } from "./medicationReviewBadges";
import { awaitsPatientInput, groupReviewItems } from "./ReviewConflictCard";
import { PillTabs } from "@/shared/components/PillTabs";
import { ISSUE_LABELS } from "./medicationReviewBadges";
import { outcomeActionLabel } from "./reviewOutcomeLabels";
import type { ReviewItem } from "@/portals/patient/api/reviewItemsApi";

interface MedicationsSectionProps {
  medications: Medication[];
  /** Opens a completed review's outcome (Module 5). */
  onReviewItem?: (reviewItemId: string) => void;
  /** "Needs your review" card: opens the Human Review Queue, where reviewing happens. */
  onOpenReviewQueue?: () => void;
  /** Resolved review items for "Completed reviews" (Module 5). Omitted or empty → the section isn't shown. */
  completedReviews?: ReviewItem[];
}

/** A selected card in the list — the platform's red selection style. */
const SELECTED_CARD = "!border-2 !border-[#F17893] !bg-[#FFF2F4] hover:!bg-[#FFF2F4]";

/** Rows per page in each medication list. */
const PAGE_SIZE = 20;

const statusLabels: Record<string, string> = {
  active: "Active", discontinued: "Discontinued",
  on_hold: "On Hold", completed: "Completed",
};

const routeLabels: Record<string, string> = {
  oral: "By Mouth", topical: "Applied to skin",
  injection: "Injection", inhalation: "Inhaled", other: "Other",
};

const CORRECTED_FIELD_LABELS: Record<string, string> = {
  medication_name: "name", dose_quantity: "dose", dose_unit: "dose unit", frequency: "frequency",
  route: "route", dosage_text: "instructions", status: "status",
};

function formatDate(dateString: string | undefined): string {
  // "" = the source never gave a usable date (see adaptMedication's toDateOnly).
  if (!dateString) return "Not available";
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const date = new Date(dateString + "T00:00:00");
  if (Number.isNaN(date.getTime())) return "Not available";
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

// Same RedIcon as DiagnosisSection
function RedIcon({ icon, size = "md" }: { icon: string; size?: "xs" | "sm" | "md" | "lg" }) {
  return (
    <div className={cn(
      "flex shrink-0 items-center justify-center rounded-xl bg-[#E4002B]/50",
      size === "xs" && "h-6 w-6",
      size === "sm" && "h-7 w-7",
      size === "md" && "h-9 w-9",
      size === "lg" && "h-11 w-11"
    )}>
      <Icon icon={icon} className={cn(
        "!text-white",
        size === "xs" && "h-3 w-3",
        size === "sm" && "h-3 w-3",
        size === "md" && "h-4 w-4",
        size === "lg" && "h-5 w-5"
      )} />
    </div>
  );
}

function StatCard({
  icon, label, value, onClick,
}: {
  icon: string; label: string; value: number;
  /** Makes the card a button (e.g. "Needs your review" opens the Human Review Queue — at any count). */
  onClick?: () => void;
}) {
  const content = (
    <>
      <div className="flex items-center gap-2">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#E4002B]/50">
          <Icon icon={icon} className="h-4 w-4 !text-white" />
        </div>
        <p className="text-left text-sm font-semibold text-[#111827]">{label}</p>
      </div>
      <p className="text-right text-3xl font-bold text-[#111827]">{value}</p>
    </>
  );
  const className =
    "flex min-h-[110px] flex-col justify-between rounded-3xl border border-[#E5E7EB] bg-white p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-lg";
  return onClick ? (
    <button type="button" onClick={onClick} className={className} aria-label={`${label}: ${value}`}>{content}</button>
  ) : (
    <div className={className}>{content}</div>
  );
}

/** Only tags that mean something to the patient: a physician's (or their own) review outcome. */
function StatusTags({ med }: { med: Medication }) {
  return (
    <>
      <OriginTag origin={med.origin} effect={med.reviewEffect} />
    </>
  );
}

function MedicationRow({
  med, isSelected, onSelect,
}: {
  med: Medication; isSelected: boolean; onSelect: () => void;
}) {
  const isActive = med.status === "active";

  return (
    <Button
      variant="outline"
      onClick={onSelect}
      className={cn(
        "!flex !h-auto !w-full !items-center !justify-between !gap-4 !whitespace-normal !rounded-2xl !bg-white !p-4 !text-left !shadow-sm hover:!bg-[#FFF8F8] hover:!shadow-md",
        // Platform selection style: red border + light red fill.
        isSelected ? SELECTED_CARD : "!border-[#E5E7EB]"
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-extrabold text-[#0A2947]">{med.name}</p>
          <span className="rounded-full bg-[#FDECEF] px-2 py-0.5 text-[10px] font-bold text-[#E4002B]">
            {isActive ? med.dosage : statusLabels[med.status]}
          </span>
          <StatusTags med={med} />
        </div>
        <p className="mt-0.5 break-words text-xs text-[#4B5563]">{med.frequency} - {med.purpose}</p>
        <p className="mt-1.5 text-xs text-[#9CA3AF]">
          Prescribed By <span className="font-semibold text-[#4B5563]">{med.prescribedBy}</span>
        </p>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1 text-xs">
        {isActive ? (
          <span className="flex items-center gap-1.5 font-semibold text-[#111827]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#16A34A]" /> Active
          </span>
        ) : (
          <>
            <span className="text-[#9CA3AF]">Last Updated</span>
            <span className="font-semibold text-[#4B5563]">{formatDate(med.endDate ?? med.prescribedDate)}</span>
          </>
        )}
      </div>
    </Button>
  );
}

function MedicationGroup({
  meds, selectedId, onSelect, label,
}: {
  meds: Medication[]; selectedId?: string; onSelect: (med: Medication) => void;
  /** Accessible name of the list (the selected splitter option). */
  label: string;
}) {
  // Long histories render a page at a time; "Show more" adds the next page.
  const [visible, setVisible] = useState(PAGE_SIZE);
  const shown = meds.slice(0, visible);
  return (
    <section className="grid gap-3" aria-label={label}>
      {meds.length > 0 ? (
        <div className="grid gap-4">
          {shown.map((med) => (
            <MedicationRow key={med.id} med={med} isSelected={med.id === selectedId} onSelect={() => onSelect(med)} />
          ))}
          {meds.length > visible && (
            <Button
              variant="outline"
              onClick={() => setVisible((v) => v + PAGE_SIZE)}
              className="!h-10 !rounded-2xl !border-[#E5E7EB] !bg-white !text-sm !font-semibold !text-[#4B5563] hover:!border-[#E4002B] hover:!text-[#E4002B]"
            >
              Show more ({meds.length - visible} more)
            </Button>
          )}
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-[#E5E7EB] bg-white py-6 text-center">
          <p className="text-xs font-semibold text-[#9CA3AF]">Nothing here yet</p>
        </div>
      )}
    </section>
  );
}

function MedicationDetailPanel({
  medication, onClose,
}: {
  medication: Medication | null; onClose: () => void;
}) {
  if (!medication) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center rounded-3xl border border-[#E5E7EB] bg-white p-6 text-center shadow-sm">
        <RedIcon icon="lucide:pill" size="lg" />
        <p className="mt-4 text-sm font-extrabold text-[#0A2947]">Select a medication</p>
        <p className="mt-1 text-xs font-semibold text-[#9CA3AF]">Choose a medication from the list to view its details</p>
      </div>
    );
  }

  return (
    <div className="rounded-3xl border border-[#E5E7EB] bg-white p-6 shadow-sm">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-4">
          <RedIcon icon="lucide:pill" size="lg" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="text-lg font-extrabold text-[#0A2947]">{medication.name}</h2>
              <span className="rounded-full bg-[#FDECEF] px-2.5 py-0.5 text-xs font-semibold text-[#E4002B]">
                {statusLabels[medication.status]}
              </span>
              <StatusTags med={medication} />
            </div>
            {medication.reviewEffect === "CORRECTED" && medication.correctedFields && medication.correctedFields.length > 0 && (
              <p className="mt-1 text-xs text-[#027A48]">
                Corrected by your physician: {medication.correctedFields.map((f) => CORRECTED_FIELD_LABELS[f] ?? f).join(", ")}
              </p>
            )}
            {medication.genericName && (
              <p className="mt-1 text-sm text-[#9CA3AF]">Generic: {medication.genericName}</p>
            )}
          </div>
        </div>
        <Button
          variant="ghost"
          onClick={onClose}
          className="!flex !h-8 !w-8 !shrink-0 !items-center !justify-center !rounded-full !p-0 !text-[#9CA3AF] hover:!bg-[#F3F4F6] hover:!text-[#111827]"
        >
          <Icon icon="lucide:x" className="h-5 w-5" />
        </Button>
      </div>

      {/* How to Take */}
      <div className="mb-6 rounded-2xl border border-[#E4002B]/15 bg-[#FFF0F3] p-5">
        <h3 className="mb-3 text-sm font-extrabold text-[#E4002B]">How to Take</h3>
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <p className="text-xs text-[#9CA3AF]">Dosage</p>
            <p className="mt-0.5 text-sm font-semibold text-[#0A2947]">{medication.dosage}</p>
          </div>
          <div>
            <p className="text-xs text-[#9CA3AF]">Frequency</p>
            <p className="mt-0.5 text-sm font-semibold text-[#0A2947]">{medication.frequency}</p>
          </div>
          <div>
            <p className="text-xs text-[#9CA3AF]">Route</p>
            <p className="mt-0.5 text-sm font-semibold text-[#0A2947]">{routeLabels[medication.route]}</p>
          </div>
        </div>
        <div className="mt-4">
          <p className="text-xs text-[#9CA3AF]">Instructions</p>
          <p className="mt-0.5 text-sm text-[#4B5563]">{medication.instructions}</p>
        </div>
      </div>

      {/* Purpose */}
      <div className="mb-6">
        <h3 className="mb-2 text-sm font-extrabold text-[#0A2947]">Purpose</h3>
        <p className="text-sm text-[#4B5563]">{medication.purpose}</p>
      </div>

      {/* Info grid */}
      {/* Two per row: four in a row squeezed the labels and values. */}
      <div className="grid grid-cols-2 gap-3">
        {[
          { icon: "lucide:calendar",  label: "Start Date",        value: formatDate(medication.startDate) },
          { icon: "lucide:user",      label: "Prescribed By",     value: medication.prescribedBy },
          ...(medication.pharmacy ? [{ icon: "lucide:building-2", label: "Pharmacy", value: medication.pharmacy }] : []),
          { icon: "lucide:refresh-cw", label: "Refills Remaining", value: medication.refillsRemaining !== null ? String(medication.refillsRemaining) : "Not specified" },
          { icon: "lucide:clock",     label: "Last Update",       value: formatDate(medication.endDate ?? medication.prescribedDate) },
        ].map(({ icon, label, value }) => (
          <div key={label} className="rounded-2xl border border-[#E5E7EB] bg-[#F9FAFB] p-3">
            <div className="flex items-start gap-1.5">
              <RedIcon icon={icon} size="xs" />
              <p className="text-xs leading-tight text-[#9CA3AF]">{label}</p>
            </div>
            <p className="mt-1.5 text-sm font-semibold text-[#0A2947]">{value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function formatIsoDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/** Resolved reviews (Module 5), collapsed by default; each row opens its outcome view. */
function CompletedReviews({ items, onOpen }: { items: ReviewItem[]; onOpen: (id: string) => void }) {
  return (
    <Collapse
      className="!rounded-2xl !border-[#E5E7EB] !bg-white [&_.ant-collapse-header]:!items-center"
      items={[
        {
          key: "completed",
          label: <span className="text-sm font-extrabold text-[#111827]">Completed reviews ({items.length})</span>,
          children: (
            <div className="grid gap-2">
              {items.map((item) => {
                const isPair = item.records.some((r) => r.recordRole === "EXISTING") && item.records.some((r) => r.recordRole === "INCOMING");
                return (
                  <Button
                    key={item.id}
                    variant="outline"
                    onClick={() => onOpen(item.id)}
                    className="!flex !h-auto !w-full !items-center !justify-between !gap-3 !whitespace-normal !rounded-2xl !border-[#E5E7EB] !bg-white !p-3 !text-left hover:!bg-[#FFF8F8]"
                  >
                    <span className="min-w-0">
                      <span className="block text-sm font-extrabold text-[#0A2947]">{ISSUE_LABELS[item.triggerType]}</span>
                      <span className="block text-xs text-[#4B5563]">
                        {item.outcome ? outcomeActionLabel(item.outcome.action, isPair) : "Reviewed by your physician"}
                      </span>
                    </span>
                    {item.outcome && <span className="shrink-0 text-xs text-[#9CA3AF]">{formatIsoDate(item.outcome.decidedAt)}</span>}
                  </Button>
                );
              })}
            </div>
          ),
        },
      ]}
    />
  );
}

type Selection = { kind: "med"; id: string } | null;
type ListFilter = "all" | "active" | "lowRefills";

export function MedicationsSection({ medications: allMedications, onReviewItem, onOpenReviewQueue, completedReviews }: MedicationsSectionProps) {
  // Records under review (Module 2) are reviewed in the Human Review Queue, not
  // here: their values aren't settled yet, so they stay out of these lists and
  // counts (the "Needs your review" card counts the ones waiting on the patient).
  // Records a physician DISMISSED (Module 5) aren't the patient's medicines any
  // more: hidden everywhere but that review's outcome. Earlier (pre-review) and
  // new medicines are one list.
  const needsYou = groupReviewItems(allMedications).filter((g) => awaitsPatientInput(g.records[0].review?.itemStatus));
  const medications = allMedications.filter(m => !m.review && m.reviewEffect !== "DISMISSED");
  const activeMeds     = medications.filter(m => m.status === "active");
  const lowRefillMeds  = activeMeds.filter(m => m.refillsRemaining !== null && m.refillsRemaining <= 1);

  const [filter, setFilter] = useState<ListFilter>("all");
  const lists: Record<ListFilter, { label: string; meds: Medication[] }> = {
    all: { label: "All medications", meds: medications },
    active: { label: "Active medications", meds: activeMeds },
    lowRefills: { label: "Low on refills", meds: lowRefillMeds },
  };
  const shown = lists[filter];

  // The first medicine is selected by default.
  const [selection, setSelection] = useState<Selection>(medications[0] ? { kind: "med", id: medications[0].id } : null);
  // Looked up from the current data every render, so a refresh never leaves a stale panel.
  const selectedMedication = selection ? medications.find((m) => m.id === selection.id) ?? null : null;
  const selectMed = (med: Medication) => setSelection({ kind: "med", id: med.id });

  return (
    <div className="min-h-full bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5">
      {/* Stats cards — "Needs your review" opens the Human Review Queue */}
      <div className="mb-5 grid gap-5 sm:grid-cols-3">
        <StatCard
          icon="lucide:circle-alert" label="Needs your review" value={needsYou.length}
          onClick={onOpenReviewQueue}
        />
        <StatCard icon="lucide:pill" label="Active Medications" value={activeMeds.length} />
        <StatCard icon="lucide:refresh-cw" label="Low on Refills" value={lowRefillMeds.length} />
      </div>

      <div className="grid gap-5 lg:grid-cols-[1.3fr_1fr]">
        <div className="grid content-start gap-5">
          {/* The platform's pill splitter picks which list is shown. */}
          <PillTabs
            label="Medication list"
            active={filter}
            onChange={setFilter}
            className="self-start justify-self-start"
            tabs={(Object.keys(lists) as ListFilter[]).map((id) => ({ id, label: `${lists[id].label} (${lists[id].meds.length})` }))}
          />
          {/* Keyed by filter so switching lists starts again from the first page. */}
          <MedicationGroup
            key={filter} label={shown.label} meds={shown.meds}
            selectedId={selectedMedication?.id} onSelect={selectMed}
          />
          {completedReviews && completedReviews.length > 0 && (
            <CompletedReviews items={completedReviews} onOpen={(id) => onReviewItem?.(id)} />
          )}
        </div>

        <div className="lg:sticky lg:top-4 lg:self-start">
          <MedicationDetailPanel medication={selectedMedication} onClose={() => setSelection(null)} />
        </div>
      </div>
    </div>
  );
}
